import type { AnyAction, Middleware, Reducer, ReducersMapObject, Store, StoreEnhancer } from 'redux';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import type { PersistConfig, Persistor } from 'redux-persist';
import { persistReducer, persistStore } from 'redux-persist';
import type { ThunkDispatch } from 'redux-thunk';
import thunkMiddleware from 'redux-thunk';

import * as reducers from '../state/reducers';
import type { YammerState } from '../state/types';

interface CreateStoreOptions<T> {
  readonly reducers?: ReducersMapObject<Partial<T>, AnyAction>;
  readonly initialState?: Partial<T>;
  readonly middleware?: Middleware<Record<string, unknown>, T>[];
  readonly persistConfig?: PersistConfig<T>;
}

export type StoreWithDispatch<T> = Store<T> & {
  readonly dispatch: ThunkDispatch<T, unknown, AnyAction>;
};
export interface CreateStoreResult<T> {
  readonly store: StoreWithDispatch<T>;
  readonly persistor?: Persistor;
}

export type CreateEnhancedStore = <T extends YammerState>(options: CreateStoreOptions<T>) => CreateStoreResult<T>;
const createEnhancedStore: CreateEnhancedStore = ({
  reducers: otherReducers = {},
  initialState = emptyState,
  middleware = [],
  persistConfig,
}) => {
  const combinedReducers: Reducer<unknown> = combineReducers({ ...otherReducers, ...reducers });

  const reducer = persistConfig ? persistReducer(persistConfig, combinedReducers) : combinedReducers;
  const enhancer = getEnhancer(middleware, process.env.NODE_ENV === 'production');

  const store = createStore(reducer, initialState, enhancer);
  const persistor = persistConfig ? persistStore(store, null) : undefined;

  return { store, persistor };
};

const getMiddleware = (otherMiddleware: Middleware[]) => [...otherMiddleware, thunkMiddleware];

const getEnhancer = (otherMiddleware: Middleware[], isProduction: boolean) =>
  getCompose(isProduction)(applyMiddleware(...getMiddleware(otherMiddleware)));

const getCompose = (isProduction: boolean) =>
  isWindowWithDevTools(window)
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ serialize: true, trace: !isProduction })
    : compose;

type WindowWithDevTools = Window & {
  // oxlint-disable-next-line typescript/no-explicit-any
  __REDUX_DEVTOOLS_EXTENSION_COMPOSE__(options: Record<string, unknown>): (...args: unknown[]) => StoreEnhancer<any>;
};

const isWindowWithDevTools = (arg: Window | WindowWithDevTools): arg is WindowWithDevTools =>
  '__REDUX_DEVTOOLS_EXTENSION_COMPOSE__' in arg;

const emptyState: Partial<YammerState> = {};

export default createEnhancedStore;
